import { ReactNode } from 'react'
import { Loader } from '@systemeio/components'
import { useRouter } from 'next/router'
import { baseServerSideProps } from 'shared/utils/base-server-side-props'
import { withDomainAssets } from 'shared/utils/with-domain-assets'
import { communityPostKeys } from 'modules/community/api/key-factory'
import { getTopicPostsByLastId } from 'modules/community/api/posts-api'
import { usePostsCommentsInfo } from 'modules/community/api/posts-comments-info'
import { useDeletePost } from 'modules/community/api/use-delete-post'
import { useDeletePostAndBanUser } from 'modules/community/api/use-delete-post-and-ban'
import { usePostAnnounceList } from 'modules/community/api/use-post-announce-list'
import { useToggleLike } from 'modules/community/api/use-toggle-like'
import AddPost from 'modules/community/components/manage-post/create-post'
import { PostPinnedList } from 'modules/community/components/pinned-post/post-pinned-list'
import { PostAnnounceSkeleton } from 'modules/community/components/post-announce/components/post-announce-skeleton'
import PostAnnounce from 'modules/community/components/post-announce/post-announce'
import { ScrollToTopButton } from 'modules/community/components/scroll-to-top-button'
import useIsUserMember from 'modules/community/hooks/use-is-user-member'

function Topic(): ReactNode {
  const router = useRouter()
  const { communityPath, topicPath } = router.query
  const { isUserMember } = useIsUserMember()

  const postsAnnounceList = usePostAnnounceList({
    queryKey: communityPostKeys.topicList(communityPath as string, topicPath as string),
    fetchPosts: (lastPostId: number | null) =>
      getTopicPostsByLastId(lastPostId || 0, communityPath, topicPath),
  })
  const postIds = postsAnnounceList.posts.map(post => post.id)
  const commentsInfoQuery = usePostsCommentsInfo({ postIds })
  const handleLikeClick = useToggleLike()
  const deletePost = useDeletePost()
  const deletePostAndBanUser = useDeletePostAndBanUser({
    communityPath: communityPath,
  })

  return (
    <>
      <div data-testid="community-route-community-topic-post">
        {isUserMember && <AddPost />}
        {postsAnnounceList.isLoading && postsAnnounceList.posts.length === 0 && (
          <>
            <PostAnnounceSkeleton key={1} />
            <PostAnnounceSkeleton key={2} />
          </>
        )}
        <PostPinnedList />
        {postsAnnounceList.posts.map(post => (
          <PostAnnounce
            key={post.id}
            post={post}
            handleLikeClick={handleLikeClick(post)}
            deletePost={() => deletePost(post.id)}
            deletePostAndBanUser={removeRecentActivities =>
              deletePostAndBanUser({ postId: post.id, userId: post.userId, removeRecentActivities })
            }
            commentsInfo={commentsInfoQuery.data?.items}
            commentsInfoError={commentsInfoQuery.error}
          />
        ))}
        {postsAnnounceList.isFetchingNextPage && (
          <div className="flex justify-center">
            <Loader size="xl" color="secondary" />
          </div>
        )}
        <div ref={postsAnnounceList.sentinelRef} />
      </div>
      <ScrollToTopButton />
    </>
  )
}

export const getServerSideProps = withDomainAssets(baseServerSideProps)

export default Topic
